import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IRequestCallback } from '@df/api/request/request.service';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { CustomformComponentDef } from '@df/catalog-ui/customform/customform.component.def';
import { ICustomformField } from '@df/catalog/customform/customform';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subject } from 'rxjs';
import { MuiInputDirective } from '../../module/legacy/forms/mui-input.directive';
import { NewsletterCheckboxesComponent } from '../../newsletter/newsletter-checkboxes.component';
import { SESSION_STORAGE_FLAG_NAME } from '../../newsletter/newsletter-form.component';
import { ActionComponent } from '../../ui/api/action.component';
import { ResultComponent } from '../../ui/api/result.component';
import { CheckboxComponent } from '../../ui/form/checkbox';
import { InputWrapBodyDirective } from '../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../ui/form/input-wrap.component';
import { RadioComponent } from '../../ui/form/radio/radio.component';

enum EAutoCompleteValues {
    'title' = 'honorific-suffix',
    'first name' = 'given-name',
    'last name' = 'family-name',
    'email' = 'email',
    'phone number' = 'tel',
    'address' = 'street-address',
    'town/city' = 'address-level2',
    'postcode' = 'postal-code'
}
@Component({
    selector: 'customform',
    templateUrl: './customform.html',
    styleUrls: ['customform.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        FormsModule,
        CommonModule,
        TranslationModule,
        ActionComponent,
        NewsletterCheckboxesComponent,
        RadioComponent,
        InputWrapComponent,
        MuiInputDirective,
        CheckboxComponent,
        ResultComponent,
        InputWrapBodyDirective
    ]
})
export class CustomformComponent extends CustomformComponentDef<{}> {
    beforeSubmitSubject = new Subject<void>();
    autoCompleteValue = EAutoCompleteValues;
    resetSubject = new Subject<void>();
    checkboxOptionTicked = false;

    private scrollToService = inject(ScrollToService);
    private sessionStorageService = inject(SessionStorageService);

    @Input()
    showBrochureCheckbox?: boolean;

    @ViewChild('newsletterCheckboxes')
    newsletterCheckboxesComponent?: NewsletterCheckboxesComponent

    override submit(onSuccess?: IRequestCallback<any>, onError?: IRequestCallback<any>) {
        if (this.identifier === 'signup' && !this.checkboxOptionTicked) {
            this.scrollToService.scrollTo('#customFormId', { offset: 200 });
            return (this.status.error = 'You need to select at least one checkbox field');
        }
        const cb =  () => super.submit(onSuccess, onError)
        if (!this.form || this.form.valid) {
            this.scrollToService.scrollTo('#customFormId', { offset: 200 });
            const promise = this.newsletterCheckboxesComponent?.submit()
            promise ? promise.finally(cb) : cb()
        } else {
            cb()
        }
    }

    protected override onModelChange(): void {
        super.onModelChange();
        this.setEmailField();
    }

    protected setEmailField(): void {
        if (this.identifier === 'signup') {
            const data = this.sessionStorageService.get(SESSION_STORAGE_FLAG_NAME);
            if (data?.email && this.emailField) {
                this.data[this.emailField.cid] = data.email;
                this.sessionStorageService.remove(SESSION_STORAGE_FLAG_NAME);
            }
        }
    }

    override reset(): void {
        this.resetSubject.next();
        super.reset();
    }

    validateCheckboxes(data: boolean) {
        this.checkboxOptionTicked = data;
    }

    override dataReset(): void {
        this.data = {};
        this.fields?.forEach(field => {
            switch (field.field_type) {
                case 'checkboxes':
                    this.data[field.cid] = field.field_options?.options
                        ? field.field_options.options.filter(i => i.checked).map(i => i.label)
                        : [];
                    break;
                case 'radio':
                    this.data[field.cid] = field.field_options?.options ? field.field_options.options.find(i => i.checked)?.label : [];
                    break;
                case 'subscription':
                    break;
                case 'address':
                    this.data[field.cid] = {};
                    break;
            }
        });
    }
    get email(): string | undefined {
        const field = this.emailField;
        if (field && this.data) {
            const email = this.data[field.cid];
            if (email && typeof email === 'string') {
                return email;
            }
        }
        return undefined;
    }
    get emailField(): ICustomformField | undefined {
        return this.fields?.find(i => i.field_type === 'email');
    }
}
